// src/components/Navbar.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom'; // Import Link for client-side routing
import logo from '../assets/reading-towers/branding/logo-circle.png';

const NavLink = ({ title, to }) => (
  <Link
    to={to}
    className="group text-gray-100 hover:text-reading-gold px-4 flex items-center h-full text-sm font-medium relative"
  >
    <span className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-[2px] bg-reading-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-center"></span>
    {title}
  </Link>
);

// Reusable DropdownLink for desktop items with dropdowns
const DesktopDropdownLink = ({ title, items = [], isExternalLink = false, itemTextLeft = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative h-full flex items-center"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="group text-gray-100 hover:text-reading-gold px-4 flex items-center h-full text-sm font-medium relative focus:outline-none"
      >
        <span className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-[2px] bg-reading-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-center"></span>
        {title}
        {items.length > 0 && (
          <svg
            className={`ml-1.5 h-4 w-4 text-gray-300 group-hover:text-reading-gold transform transition-transform duration-150 ${
              isOpen ? 'rotate-180' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
      {isOpen && items.length > 0 && (
        <div className="absolute left-0 top-full mt-0 w-max min-w-64 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5">
          {items.map((item) => (
            isExternalLink || item.isExternal ? (
              <a
                key={item.name}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className={`block px-4 py-2 text-sm ${itemTextLeft ? 'text-left' : 'text-center'} text-gray-700 hover:bg-gray-100 hover:text-reading-gold whitespace-nowrap`}
              >
                {item.name}
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.to}
                className={`block px-4 py-2 text-sm ${itemTextLeft ? 'text-left' : 'text-center'} text-gray-700 hover:bg-gray-100 hover:text-reading-gold whitespace-nowrap`}
              >
                {item.name}
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
};

// Component for links in the mobile menu (content is within a bg-white panel, so text colors are fine)
const MobileNavLink = ({ to, children, onClick, isExternal = false }) => (
  isExternal ? (
    <a
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-reading-gold hover:bg-gray-50 rounded-md"
    >
      {children}
    </a>
  ) : (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-reading-gold hover:bg-gray-50 rounded-md"
    >
      {children}
    </Link>
  )
);

// Component for dropdowns in the mobile menu (content is within a bg-white panel, so text colors are fine)
const MobileDropdownLink = ({ title, items = [], closeMobileMenu, isExternalLink = false }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
        className="w-full flex items-center justify-between px-3 py-3 text-base font-medium text-gray-700 hover:text-reading-gold hover:bg-gray-50 rounded-md"
      >
        <span>{title}</span>
        <svg
          className={`ml-1.5 h-5 w-5 transform transition-transform duration-150 ${isSubMenuOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
        </svg>
      </button>
      {isSubMenuOpen && (
        <div className="pl-4 mt-1 space-y-1">
          {items.map((item) => (
            <MobileNavLink key={item.name} to={item.to} onClick={closeMobileMenu} isExternal={isExternalLink || item.isExternal}>
              {item.name}
            </MobileNavLink>
          ))}
        </div>
      )}
    </div>
  );
};


const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const buildingItems = [
    { name: 'Amenities', to: '/amenities' },
    { name: 'Residences', to: '/residences' },
    { name: 'Neighborhood', to: '/neighborhood' },
  ];

  const virtualToursItems = [
    {
      name: "2 bd Corner Unit - City, Mountain View",
      to: "https://my.matterport.com/show/?m=RoxcTL9Qx8Q", //corner windows unfurnished
      isExternal: true,
    },
    {
      name: "2 bd Unit - City View",
      to: "https://my.matterport.com/show/?m=VdDj1pk43zy&play=1", //-04
      isExternal: true,
    },
    {
      name: "1 bd Unit - City, Mountain View",
      to: "https://my.matterport.com/show/?m=cKmcGW7kfbM&play=1", //-18
      isExternal: true,
    },
    {
      name: "Studio Unit - City, Mountain View",
      to: "https://my.matterport.com/show/?m=hAdfXduqaL2&play=1", // -09
      isExternal: true,
    },
    {
     name: "Lounge",
     to: "https://my.matterport.com/show/?m=6KE847Ymfid&play=1", // lounge
     isExternal: true, 
    },
  ];

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-[rgb(63,64,71)] shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-20">
        <div className="flex items-center text-center justify-between h-full">
          <div className="flex-shrink-0">
            <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-3">
              <img className="h-11 w-11 md:h-12 md:w-12 xl:h-14 xl:w-14 rounded-full object-contain" src={logo} alt="Reading Tower Apartments" />
              <span
                className="hidden sm:block text-reading-gold text-xl md:text-2xl xl:text-2xl font-normal uppercase"
                style={{ fontFamily: 'Garamond, "EB Garamond", Georgia, serif' }}
              >
                Reading Tower
              </span>
            </Link>
          </div>

          {/* Desktop Links use updated NavLink and DesktopDropdownLink with lighter text */}
          <div className="hidden lg:flex items-center h-full space-x-5">
            <DesktopDropdownLink title="The Tower" items={buildingItems} />
            <NavLink title="Availability" to="/availability" />
            <NavLink title="Gallery" to="/gallery" />
            <DesktopDropdownLink title="Virtual Tours" items={virtualToursItems} isExternalLink={true} itemTextLeft={true} />
            <NavLink title="Contact Us" to="/contact-us" />
          </div>

          {/* Desktop Buttons: These have their own backgrounds, so text colors are fine */}
          <div className="hidden lg:flex items-center space-x-2 ">
            <Link
              to="/contact-us"
              className="px-4 py-2.5 text-sm font-medium text-white bg-reading-gray hover:bg-reading-gold rounded-md shadow-sm transition-colors duration-150"
            >
              Schedule Tour
            </Link>
            <Link
              to="/availability"
              className="px-4 py-2.5 text-sm font-medium text-reading-gray bg-white hover:bg-reading-gold hover:text-white border border-reading-gray hover:border-reading-gold rounded-md shadow-sm transition-colors duration-150"
            >
              Apply Now
            </Link>
          </div>

          {/* Mobile Buttons always visible */}
          <div className="lg:hidden flex items-center space-x-2">
            <Link
              to="/contact-us"
              className="px-2 py-2 text-xs font-medium text-white bg-reading-gray hover:bg-reading-gold rounded-md shadow-sm transition-colors duration-150"
            >
              Schedule Tour
            </Link>
            <Link
              to="/availability"
              className="px-2 py-2 text-xs font-medium text-reading-gray bg-white hover:bg-reading-gold hover:text-white border border-reading-gray hover:border-reading-gold rounded-md shadow-sm transition-colors duration-150"
            >
              Apply
            </Link>
          </div>

          {/* Mobile Menu Button (Hamburger) */}
          <div className="lg:hidden flex items-center">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              // MODIFIED: Icon color and hover states for dark navbar
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-200 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-reading-gray"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              <svg className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
              <svg className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel remains bg-white, text inside is fine */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-20 inset-x-0 z-40 transform origin-top shadow-lg" id="mobile-menu">
          <div className="bg-white pt-2 pb-3 space-y-1 sm:px-3 px-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <MobileDropdownLink title="The Tower" items={buildingItems} closeMobileMenu={closeMobileMenu} />
            <MobileNavLink to="/availability" onClick={closeMobileMenu}>Availability</MobileNavLink>
            <MobileNavLink to="/gallery" onClick={closeMobileMenu}>Gallery</MobileNavLink>
            <MobileDropdownLink title="Virtual Tours" items={virtualToursItems} closeMobileMenu={closeMobileMenu} isExternalLink={true} />
            <MobileNavLink to="/contact-us" onClick={closeMobileMenu}>Contact Us</MobileNavLink>

            <div className="pt-4 pb-2 border-t border-gray-200 space-y-2">
              <MobileNavLink to="/contact-us" onClick={closeMobileMenu}>
                <span className="block w-full text-center px-4 py-2 text-white bg-reading-gray hover:bg-reading-gold rounded-md shadow-sm">Schedule Tour</span>
              </MobileNavLink>
              <MobileNavLink to="/availability" onClick={closeMobileMenu}>
                 <span className="block w-full text-center px-4 py-2 text-reading-gray bg-white hover:bg-reading-gold hover:text-white border border-reading-gray hover:border-reading-gold rounded-md shadow-sm">Apply Now</span>
              </MobileNavLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
